import { useState, type FormEvent } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth, useAuthState } from "@atlas/auth";
import { useEffect } from "react";

/**
 * Login page with email/password form and social login buttons
 * - Custom form (not Cognito Hosted UI)
 * - Google and Apple social login buttons
 * - Link to forgot-password and signup
 * - Redirects if already authenticated
 */
export function LoginPage(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuthState();
  const { signIn, signInWithGoogle, signInWithApple } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get the intended destination from location state
  const from = (location.state as { from?: string })?.from ?? "/";

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from]);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await signIn(email, password);

      if (result.isSignedIn) {
        navigate(from, { replace: true });
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to sign in";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async (): Promise<void> => {
    try {
      await signInWithGoogle();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to sign in with Google";
      setError(errorMessage);
    }
  };

  const handleAppleSignIn = async (): Promise<void> => {
    try {
      await signInWithApple();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to sign in with Apple";
      setError(errorMessage);
    }
  };

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <p className="text-text-secondary text-sm">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-xl font-semibold text-center mb-6 text-text-primary">
          Sign in to Atlas
        </h1>

        {error && (
          <div className="alert-error mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label
              htmlFor="email"
              className="block mb-1.5 text-xs font-medium text-text-secondary"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="input-field"
            />
          </div>

          <div className="mb-3">
            <label
              htmlFor="password"
              className="block mb-1.5 text-xs font-medium text-text-secondary"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="input-field"
            />
          </div>

          <div className="mb-4 text-right">
            <Link
              to="/forgot-password"
              className="link text-xs"
            >
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary-lg w-full"
          >
            {isSubmitting ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="flex items-center my-5">
          <div className="flex-1 h-px bg-border" />
          <span className="mx-3 text-text-tertiary text-xs">or</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <div className="flex flex-col gap-2">
          <button
            onClick={handleGoogleSignIn}
            type="button"
            className="btn-secondary w-full flex items-center justify-center gap-2"
          >
            <svg width="14" height="14" viewBox="0 0 18 18">
              <path
                fill="#4285F4"
                d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
              />
              <path
                fill="#34A853"
                d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
              />
              <path
                fill="#FBBC05"
                d="M3.964 10.707A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"
              />
              <path
                fill="#EA4335"
                d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"
              />
            </svg>
            Continue with Google
          </button>

          <button
            onClick={handleAppleSignIn}
            type="button"
            className="inline-flex items-center justify-center gap-2 h-8 px-3 bg-text-primary text-white text-[0.8125rem] font-medium rounded-md border-none cursor-pointer hover:opacity-90 transition-opacity duration-150"
          >
            <svg width="14" height="14" viewBox="0 0 18 18">
              <path
                fill="currentColor"
                d="M14.94 9.88c-.023-2.345 1.911-3.474 1.998-3.529-.923-1.35-2.36-1.535-2.868-1.556-1.218-.126-2.386.722-3.006.722-.617 0-1.574-.705-2.588-.686-1.33.02-2.558.778-3.244 1.974-1.386 2.405-.354 5.961.99 7.911.66.952 1.443 2.021 2.473 1.983 .995-.04 1.37-.643 2.573-.643 1.2 0 1.544.643 2.596.622 1.069-.02 1.746-1.01 2.4-1.966.76-1.101 1.072-2.17 1.09-2.225-.024-.01-2.09-.803-2.114-3.187v.001zM12.98 3.27c.536-.67.9-1.588.8-2.51-.773.032-1.716.52-2.273 1.167-.496.576-.936 1.51-.82 2.398.864.068 1.748-.436 2.293-1.055z"
              />
            </svg>
            Continue with Apple
          </button>
        </div>

        <p className="text-center mt-6 text-xs text-text-secondary">
          Don't have an account?{" "}
          <Link to="/signup" className="link">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
